/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: branding-media-search-grid-component.ts
 *
 * Created by kyang on 11/3/2020.
 */

function BrandingMediaSearchGridController (
    this: JS5Object,
    _ :JS5Object,
    $location: JS5Object,
    $routeParams: JS5Object,
    BRAND_ASSET_MAP_TYPES: JS5Object,
    BrandingAssetService: JS5Object,
    BrandingMediaViewDataTransformer: JS5Object,
    BrandingViewService: JS5Object,
    uiGridConstants: JS5Object,
) {
    enum ContainerType {
        PRE_ROLL = 'preRolls',
        POST_ROLL = 'postRolls',
        BREAK_PRE_POST_ROLL = 'breakPrePostRolls',
        ARTWORK = 'artwork',
        WATERMARK = 'watermarks',
        RATING_OVERLAY = 'ratingOverlays',
    }

    const isBreakPrePostRoll = () => {
        return $routeParams.type === ContainerType.BREAK_PRE_POST_ROLL
    };


    //======//
    // Grid //
    //======//

    const getVisibleAssetForCell = (rootAsset: JS5Object, colField: string) => {
        switch (this.brandingConfig.type) {
            case ContainerType.PRE_ROLL:
            case ContainerType.POST_ROLL:
            case ContainerType.WATERMARK:
                return rootAsset;
            case ContainerType.BREAK_PRE_POST_ROLL:
                return (colField.startsWith('preRoll')) ? rootAsset.preRoll : rootAsset.postRoll;
            case ContainerType.ARTWORK:
            case ContainerType.RATING_OVERLAY:
                return null;
            default:
                console.warn(`Did not recognize type for branding asset container type: ${this.brandingConfig.type}`);
                return null;
        }
    };

    this.assetHasInfo = (rootAsset: JS5Object, colField: string) => {
        let asset = getVisibleAssetForCell(rootAsset, colField);
        return BrandingAssetService.assetWasIngested(asset);
    };

    this.assetHasPreview = (rootAsset: JS5Object, colField: string) => {
        let asset = getVisibleAssetForCell(rootAsset, colField);
        return BrandingAssetService.assetHasProxy(asset);
    };

    this.hasEitherPrePostRollField = (row: JS5Object, col: JS5Object) => {
        return (
            !_.isNil(_.get(row.entity, col.colDef.preRollField)) ||
            !_.isNil(_.get(row.entity, col.colDef.postRollField))
        );
    };

    this.getPreRollField = (row: JS5Object, col: JS5Object) => {
        return _.get(row.entity, col.colDef.preRollField);
    };

    this.getPostRollField = (row: JS5Object, col: JS5Object) => {
        return _.get(row.entity, col.colDef.postRollField);
    };

    const getColumnDefs = () => {
        switch (this.brandingConfig.type) {
            case ContainerType.PRE_ROLL:
            case ContainerType.POST_ROLL:
                return BrandingMediaViewDataTransformer.prePostRolls;
            case ContainerType.BREAK_PRE_POST_ROLL:
                return BrandingMediaViewDataTransformer.breakPrePostRolls;
            case ContainerType.ARTWORK:
                return BrandingMediaViewDataTransformer.artwork;
            case ContainerType.WATERMARK:
                return BrandingMediaViewDataTransformer.watermarks;
            case ContainerType.RATING_OVERLAY:
                return BrandingMediaViewDataTransformer.ratingOverlays;
        }
    };

    this.gridOptions = {
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: false,
        enableColumnResizing: true,
        enablePaginationControls: false,
        enableSorting: false,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        enableHorizontalScrollbar: uiGridConstants.ALWAYS,
        enableVerticalScrollbar: uiGridConstants.ALWAYS,
        rowHeight: 30,
        multiSelect: false,
        columnDefs: getColumnDefs(),
        rowTemplate: 'components/visual/grid/searchview-component-row-template.html'
    };

    this.onRowDblClick = (row: JS5Object) => {
        if (!this.isDisabled) {
            let targetPath: string;
            let assetRouteId = BrandingAssetService.getRootAssetRouteId(this.brandingConfig.type, row.entity);
            let basePath = this.brandingConfig.parentLocation;
            if (this.brandingConfig.groupName === 'networks') {
                basePath += '/' + this.brandingConfig.type;
            }
            if (this.brandingConfig.type === ContainerType.RATING_OVERLAY) {
                targetPath = `${basePath}/edit/${assetRouteId}`;
            } else {
                let currentPath = `${basePath}/edit/${this.container.id}/acceptProfiles`;
                targetPath = `${currentPath}/edit/${assetRouteId}`;
            }
            $location.path(targetPath);
        }
        if (this.onRowClicked) {
            this.onRowClicked(row.entity);
        }
    };

    this.openAssetPreviewModal = (rootAsset: JS5Object, colField: string) => {
        let asset = getVisibleAssetForCell(rootAsset, colField);
        switch (this.brandingConfig.type) {
            case ContainerType.PRE_ROLL:
            case ContainerType.POST_ROLL:
            case ContainerType.BREAK_PRE_POST_ROLL:
                BrandingViewService.openAssetPreviewModal(asset, 'video');
                break;
            case ContainerType.WATERMARK:
                BrandingViewService.openAssetPreviewModal(asset, 'watermark');
                break;
            default:
                console.warn('Tried to show asset preview modal for unknown asset type');
                break;
        }
    };

    this.openAssetInfoModal = (rootAsset: JS5Object, colField: string) => {
        let asset = getVisibleAssetForCell(rootAsset, colField);
        switch (this.brandingConfig.type) {
            case ContainerType.PRE_ROLL:
            case ContainerType.POST_ROLL:
            case ContainerType.BREAK_PRE_POST_ROLL:
                BrandingViewService.openAssetInfoModal(asset, 'video');
                break;
            case ContainerType.WATERMARK:
                BrandingViewService.openAssetInfoModal(asset, 'watermark');
                break;
            default:
                console.warn('Tried to show asset preview modal for unknown asset type');
                break;
        }
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    //
    // Remove season assets that don't match the current season,
    // or volume assets that don't match the current volume, or
    // both season and volume assets when we're not working on a
    // season or volume child container.
    //
    const removeIrrelevantAssets = (): void => {
        if (isBreakPrePostRoll()) {
            this.container.breakPrePostRolls = BrandingAssetService.filterBrandingAssets(
                this.brandingConfig, this.container.breakPrePostRolls
            );
        } else {
            this.container.assets = BrandingAssetService.filterBrandingAssets(
                this.brandingConfig, this.container.assets
            );
        }
    };

    const setAssetCounts = (rootAsset: JS5Object) => {
        switch (this.brandingConfig.type) {
            case ContainerType.PRE_ROLL:
            case ContainerType.POST_ROLL:
                rootAsset.numberOfAudios = _.size(rootAsset.audios);
                rootAsset.numberOfTexts = _.size(rootAsset.texts);
                rootAsset.numberOfAssets = (
                    rootAsset.numberOfAudios +
                    rootAsset.numberOfTexts +
                    ((!!rootAsset.id) ? 1 : 0)
                );
                break;
            case ContainerType.BREAK_PRE_POST_ROLL:
                rootAsset.numberOfBreakPreRollTexts = _.size(rootAsset.preRoll.texts);
                rootAsset.numberOfBreakPreRollAudios = _.size(rootAsset.preRoll.audios);
                rootAsset.numberOfBreakPostRollTexts = _.size(rootAsset.postRoll.texts);
                rootAsset.numberOfBreakPostRollAudios = _.size(rootAsset.postRoll.audios);
                rootAsset.numberOfAssets = (
                    rootAsset.numberOfBreakPostRollTexts +
                    rootAsset.numberOfBreakPreRollAudios +
                    rootAsset.numberOfBreakPostRollAudios +
                    rootAsset.numberOfBreakPreRollTexts +
                    ((!!rootAsset.preRoll.id) ? 1 : 0) +
                    ((!!rootAsset.postRoll.id) ? 1 : 0)
                );
                break;
            case ContainerType.ARTWORK:
                rootAsset.numberOfAssets = _.size(rootAsset.assets);
                break;
            case ContainerType.WATERMARK:
                rootAsset.numberOfAssets = (!!rootAsset.virtualPath) ? 1 : 0;
                break;
            case ContainerType.RATING_OVERLAY:
                rootAsset.numberOfAssets = _.size(rootAsset.images);
                break;
        }
    };

    const getAssetObjectForAcceptProfile = (acceptProfile: JS5Object) => {
        let rootAsset = BrandingAssetService.getRootBrandingAsset(
            this.brandingConfig, this.container, acceptProfile
        );
        setAssetCounts(rootAsset);
        return rootAsset;
    };

    //
    // This will return a list of asset objects, one per
    // applicable MAP. MAPs which do not currently have a
    // corresponding asset in this container are populated with an
    // empty/blank asset object.
    //
    const createFullAssetList = (mediaAcceptProfiles: JS5Object[]) => {
        let rootAssets: JS5Object[] = [];

        _.chain(mediaAcceptProfiles)
            .filter({type: BRAND_ASSET_MAP_TYPES[this.brandingConfig.type]})
            .forEach((acceptProfile: JS5Object) => {
                let rootAsset = getAssetObjectForAcceptProfile(acceptProfile);
                rootAssets.push(rootAsset);
            })
            .value();

        return rootAssets;
    };

    const getRootAssets = (mediaAcceptProfiles: JS5Object[]) => {
        removeIrrelevantAssets();
        return createFullAssetList(mediaAcceptProfiles);
    };

    const filterRootAssets = (rootAssets: JS5Object[]) => {
        return _.filter(rootAssets, (rootAsset: JS5Object) => {
            return !this.hideEmpty || rootAsset.numberOfAssets > 0;
        });
    };

    this.$onChanges = () => {
        if (this.brandingConfig && this.container && this.mediaAcceptProfiles) {
            let rootAssets = getRootAssets(this.mediaAcceptProfiles);
            this.gridOptions.data = filterRootAssets(rootAssets);
        }
    };
}

angular
    .module('prism-ui.components.branding')
    .component('brandingMediaSearchGrid', {
        templateUrl: 'components/branding/editView/components/searchGrid/branding-media-search-grid-template.html',
        controller: BrandingMediaSearchGridController,
        bindings: {
            brandingConfig: '<',
            hideEmpty: '<',
            container: '<',
            mediaAcceptProfiles: '<',
            isDisabled: '<ngDisabled',
            onRowClicked: '&?',
        }
    });
